/**
 * Customer avatar markup for search pickers / result dropdowns.
 *
 * Companies render a building icon, individuals render an initials
 * monogram — a "person" monogram on a company reads as wrong, so we
 * mirror the convention used by B2B CRMs (building = organisation,
 * monogram = person). Both sit in the same primary-coloured circle so
 * customer pickers stay visually consistent with the cleaner picker.
 */

// Same building glyph as the Settings → Company card (templates/office/
// settings/index.html) so the "organisation" mark is consistent across the app.
const BUILDING_ICON = `<svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="m4 13h3v2h-3zm5 2h3v-2h-3zm-5 4h3v-2h-3zm5 0h3v-2h-3zm-5-12h3v-2h-3zm5 0h3v-2h-3zm-5 4h3v-2h-3zm5 0h3v-2h-3zm15-3v16h-24v-21a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v2h5a3 3 0 0 1 3 3zm-10-5a1 1 0 0 0 -1-1h-10a1 1 0 0 0 -1 1v19h12zm8 5a1 1 0 0 0 -1-1h-5v15h6zm-4 7h2v-2h-2zm0 4h2v-2h-2zm0-8h2v-2h-2z"/></svg>`;

/**
 * Two-letter monogram from a name ("Marie Dupont" → "MD").
 * @param {string} name
 * @returns {string}
 */
function initialsOf(name) {
  return (name || '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

/**
 * Build the avatar HTML for a customer search result.
 * @param {string} name - Customer display name (used for the monogram).
 * @param {string} [customerType] - 'company' | 'individual'. Anything
 *   other than 'company' falls back to the initials monogram so partial
 *   or legacy payloads still render sensibly.
 * @returns {string} HTML string for a w-7 h-7 avatar circle.
 */
export function customerAvatarHtml(name, customerType) {
  const inner =
    customerType === 'company'
      ? BUILDING_ICON
      : `<span class="text-xs font-medium text-white">${initialsOf(name)}</span>`;
  return `<div class="w-7 h-7 rounded-full flex items-center justify-center mr-2.5 flex-shrink-0 bg-primary-600">${inner}</div>`;
}

export default customerAvatarHtml;
